import CONFIG from './config'

/**
 * Convert UTC date to IST (Asia/Kolkata timezone)
 */
export function convertToIST(utcDate) {
  const date = new Date(utcDate)
  return new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }))
}

/**
 * Format timestamp based on user preference
 */
export function formatTimestamp(date, format = '12hr') {
  const istDate = convertToIST(date)

  if (format === '24hr') {
    return istDate.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  return istDate.toLocaleTimeString('en-US', {
    hour12: true,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

/**
 * Format full date and time
 */
export function formatDateTime(date, timeFormat = '12hr') {
  const istDate = convertToIST(date)
  const dateStr = istDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
  const timeStr = formatTimestamp(date, timeFormat)
  return `${dateStr} ${timeStr}`
}

/**
 * Format date and time for export
 */
export function formatDateTimeForExport(date, timeFormat = '12hr') {
  const istDate = convertToIST(date)
  const dateStr = istDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  })
  const timeStr = formatTimestamp(date, timeFormat)
  return `${dateStr} - ${timeStr}`
}

/**
 * Format relative time
 */
export function formatRelativeTime(date) {
  const istDate = convertToIST(date)
  const now = new Date()
  const diffMs = now - istDate
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return 'just now'
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`

  return formatDateTime(date)
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text, showToast) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text)
      showToast?.('Copied to clipboard!', 'success')
      return true
    }

    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    document.body.appendChild(textArea)
    textArea.select()

    try {
      document.execCommand('copy')
      showToast?.('Copied to clipboard!', 'success')
      return true
    } catch (err) {
      showToast?.('Failed to copy', 'error')
      return false
    } finally {
      document.body.removeChild(textArea)
    }
  } catch (error) {
    console.error('Copy failed:', error)
    showToast?.('Failed to copy', 'error')
    return false
  }
}

/**
 * Export logs in various formats
 */
export function exportLogs(logs, format = 'txt', filename = 'logs', showToast, timeFormat = '12hr') {
  let content, mimeType, extension

  switch (format) {
    case 'json':
      content = JSON.stringify(logs, null, 2)
      mimeType = 'application/json'
      extension = 'json'
      break

    case 'csv':
      const headers = ['Timestamp', 'Level', 'Message']
      const csvRows = [headers.join(',')]
      logs.forEach(log => {
        const row = [
          `"${formatDateTimeForExport(log.timestamp, timeFormat)}"`,
          `"${log.level}"`,
          `"${log.message.replace(/"/g, '""')}"`
        ]
        csvRows.push(row.join(','))
      })
      content = csvRows.join('\n')
      mimeType = 'text/csv'
      extension = 'csv'
      break

    case 'txt':
    default:
      content = logs.map(log =>
        `[${formatDateTimeForExport(log.timestamp, timeFormat)}] [${log.level}] ${log.message}`
      ).join('\n')
      mimeType = 'text/plain'
      extension = 'txt'
      break
  }

  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${filename}.${extension}`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  showToast?.(`Exported ${logs.length} logs as ${extension.toUpperCase()}`, 'success')
}

/**
 * Debounce function
 */
export function debounce(func, delay) {
  let timeoutId
  return function (...args) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(this, args), delay)
  }
}

/**
 * Escape HTML to prevent XSS
 */
export function escapeHtml(text) {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

/**
 * Parse ANSI color codes and text modifiers
 */
export function parseAnsiColors(text) {
  if (!text) return ''
  const ansiRegex = /(?:\x1B|\\u001b|\\x1b|\\e|\\033)?\[(\d+(?:;\d+)*)m/g

  const colorMap = {
    30: 'ansi-black', 31: 'ansi-red', 32: 'ansi-green', 33: 'ansi-yellow',
    34: 'ansi-blue', 35: 'ansi-magenta', 36: 'ansi-cyan', 37: 'ansi-white',
    90: 'ansi-bright-black', 91: 'ansi-bright-red', 92: 'ansi-bright-green',
    93: 'ansi-bright-yellow', 94: 'ansi-bright-blue', 95: 'ansi-bright-magenta',
    96: 'ansi-bright-cyan', 97: 'ansi-bright-white'
  }

  const styleMap = {
    1: 'ansi-bold',
    3: 'ansi-italic',
    4: 'ansi-underline'
  }

  let activeClasses = []
  let result = ''
  let lastIndex = 0

  const escapedText = escapeHtml(text)

  escapedText.replace(ansiRegex, (match, codes, offset) => {
    result += escapedText.substring(lastIndex, offset)
    lastIndex = offset + match.length

    const codeList = codes.split(';').map(c => parseInt(c))

    for (const code of codeList) {
      if (code === 0) {
        result += '</span>'.repeat(activeClasses.length)
        activeClasses = []
      } else if (colorMap[code]) {
        activeClasses.push(colorMap[code])
        result += `<span class="${colorMap[code]}">`
      } else if (styleMap[code]) {
        activeClasses.push(styleMap[code])
        result += `<span class="${styleMap[code]}">`
      }
    }

    return ''
  })

  result += escapedText.substring(lastIndex)
  result += '</span>'.repeat(activeClasses.length)

  return result
}

/**
 * Truncate string with ellipsis
 */
export function truncate(str, maxLength = 20) {
  if (str.length <= maxLength) return str
  return str.substring(0, maxLength - 3) + '...'
}
